import { Grid, Card, CardContent, Typography, IconButton, Tooltip, Divider, Box, alpha, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouteIcon from '@mui/icons-material/Route';
import PersonIcon from '@mui/icons-material/Person';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';

import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
// import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import BatterySaverIcon from '@mui/icons-material/BatterySaver';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import SpeedIcon from '@mui/icons-material/Speed';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { useLanguage } from '../../utils/i18n';

const ReportCards = ({ category }) => {
  const { t } = useLanguage();
  const theme = useTheme();
  const navigate = useNavigate();

  const groupTitles = {
    fleet: t('reports.fleetManagement', 'Fleet Management'),
    fuel: t('reports.fuelManagement', 'Fuel Management'),
    ritase: t('reports.ritase', 'Ritase'),
    fleet_ev: t('reports.evFleetEnergyReport', 'EV Fleet Energy Report')
  };

  const categories = {
    fleet: [
      {
        id: 'veh_rep',
        path: '/data-reports?name=vehicle_report',
        title: t('notification.vehicle', 'Vehicle'),
        desc: t('reports.vehicleDesc', 'Detailed vehicle travel history with vehicle location, travel distance, and trip duration.'),
        icon: <DirectionsCarIcon fontSize="medium" color="primary" />,
        color: theme.palette.primary.main
      },
      {
        id: 'drv_rep',
        path: '/data-reports?name=driver_report',
        title: t('reportFatigue.driverCol', 'Driver'),
        desc: t('reports.driverDesc', "Every driver's operational details, including travel distance, total travel and stop duration."),
        icon: <PersonIcon fontSize="medium" color="primary" />,
        color: theme.palette.primary.main
      },
      {
        id: 'rout_rep',
        path: '/data-reports?name=trip_report',
        title: t('reports.trip', 'Trip'),
        desc: t(
          'reports.tripDesc',
          'Detailed data is obtained from vehicle records regarding trip history, distance traveled, and trip duration.'
        ),
        icon: <RouteIcon fontSize="medium" color="primary" />,
        color: theme.palette.primary.main
      },
      {
        id: 'sensor_speed',
        path: '/report-sensor?name=report-sensor',
        title: t('reports.sensorSpeed', 'Sensor Speed'),
        desc: t(
          'reports.sensorSpeedDesc',
          'Sensor accumulation for Over Speed, Under Speed, Harsh Acceleration, Harsh Braking, and Daily Speed per Vehicle.'
        ),
        icon: <SpeedIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />,
        color: theme.palette.primary.main
      },
      {
        id: 'sensor_waypoint',
        path: '/report-waypoint?name=report-waypoint',
        title: t('reports.sensorWaypoint', 'Sensor Waypoint'),
        desc: t(
          'reports.sensorWaypointDesc',
          'Comprehensive data of vehicle location history, including telemetry, sensor status, temperature, and Canbus details.'
        ),
        icon: <GpsFixedIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />,
        color: theme.palette.primary.main
      }
    ],
    fuel: [
      {
        id: 'fuel_sum',
        path: '/vehicle-fuel-summary?name=vehicle-fuel-summary',
        title: t('reports.fuelSummary', 'Fuel Summary'),
        desc: t('reports.fuelSummaryDesc', 'Fuel volume during estimated refueling, reduction, usage, fuel efficiency, and total cost.'),
        icon: <LocalGasStationIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />,
        color: theme.palette.primary.main
      },
      {
        id: 'fuel_daily',
        path: '/daily-fuel-summary?name=daily-fuel-summary',
        title: t('reports.dailySummary', 'Daily Summary'),
        desc: t(
          'reports.dailySummaryDesc',
          'Daily accumulation of fuel: estimated refueling, reduction, usage, efficiency, and total cost.'
        ),
        icon: <LocalGasStationIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />,
        color: theme.palette.primary.main
      },
      {
        id: 'fuel_hist',
        path: '/vehicle-fuel-history?name=vehicle-fuel-history',
        title: t('reports.fuelHistory', 'Fuel History'),
        desc: t('reports.fuelHistoryDesc', 'Fuel decrease or increase with speed info, ACC on/off, and location.'),
        icon: <LocalGasStationIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />,
        color: theme.palette.primary.main
      }
    ],
    fleet_ev: [
      {
        id: 'battery-management',
        path: '/battery-management?name=battery-management',
        title: t('reports.batteryManagement', 'Battery Management (Dummy)'),
        desc: t('reports.batteryManagementDesc', 'Detailed EV battery activity including energy usage (kWh).'),
        icon: <BatteryChargingFullIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />,
        color: theme.palette.primary.main
      },
      {
        id: 'charging-management',
        path: '/charging-management?name=charging-management',
        title: t('reports.chargingManagement', 'Charging Management (Dummy)'),
        desc: t('reports.chargingManagementDesc', 'Detailed battery charging activity for Electric Vehicles.'),
        icon: <BatterySaverIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />,
        color: theme.palette.primary.main
      }
    ],
    ritase: [
      {
        id: 'ritase_sum',
        path: '/data-reports?name=ritase_summary',
        title: t('reports.ritaseSummary', 'Ritase Summary'),
        desc: t('reports.ritaseSummaryDesc', 'Brief overview of fuel volume, usage, efficiency, and total cost during refuel.'),
        icon: <RouteIcon fontSize="medium" color="primary" />,
        color: theme.palette.primary.main
      },
      {
        id: 'ritase_details',
        path: '/data-reports?name=ritase_details',
        title: t('reports.ritaseDetails', 'Ritase Details'),
        desc: t('reports.ritaseDetailsDesc', 'Displays daily fuel data in detail, including consumption and efficiency.'),
        icon: <RouteIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />,
        color: theme.palette.primary.main
      }
    ]
  };

  const groupKeys = category === 'all' ? Object.keys(categories) : [category];

  return (
    <Box sx={{ pb: 4 }}>
      {groupKeys.map((group) => (
        <Box key={group}>
          {category === 'all' && (
            <Box mb={3}>
              <Typography variant="h5" fontWeight={800} color="text.primary" sx={{ letterSpacing: '-0.5px' }}>
                {groupTitles[group] || group}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('reports.selectReportDesc', 'Select one of the reports below to see detailed analytical data.')}
              </Typography>
            </Box>
          )}

          <Grid container spacing={3} alignItems="stretch">
            {categories[group].map((item) => (
              <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={item.id}>
                <Card
                  onClick={() => navigate(item.path)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    cursor: 'pointer',
                    position: 'relative',
                    bgcolor: 'background.paper',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.04)', // Soft shadow
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease-in-out',
                    overflow: 'hidden', // Untuk menjaga bottom border accent
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0px 12px 28px rgba(0, 0, 0, 0.12)',
                      borderColor: 'transparent', // Hilangkan border default saat hover
                      '& .hover-arrow': {
                        transform: 'translateX(4px)',
                        opacity: 1
                      }
                    },
                    // Bottom border accent color
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      backgroundColor: item.color,
                      opacity: 0.8
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
                      {/* Icon Container with soft background */}
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: alpha(item.color, 0.1), // Soft transparent background
                          color: item.color
                        }}
                      >
                        {item.icon}
                      </Box>

                      <Tooltip title={t('reports.markAsFavorite', 'Mark as Favorite')}>
                        <IconButton
                          size="small"
                          sx={{ color: 'text.disabled', '&:hover': { color: '#FFC107' } }}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Logic untuk favorit bisa ditaruh di sini
                          }}
                        >
                          <StarBorderIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: 'text.primary' }}>
                      {item.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.6,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {item.desc}
                    </Typography>

                    {/* Akses Visual (Panah bawah saat hover) */}
                    <Box sx={{ mt: 'auto', pt: 2, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="button" sx={{ color: item.color, fontWeight: 600, textTransform: 'none' }}>
                        {t('reports.viewReport', 'View Report')}
                      </Typography>
                      <ChevronRightIcon
                        className="hover-arrow"
                        sx={{
                          color: item.color,
                          fontSize: 20,
                          ml: 0.5,
                          opacity: 0.5,
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Menambahkan Divider antar grup jika kategorinya 'all' dan bukan grup terakhir */}
          {category === 'all' && group !== groupKeys[groupKeys.length - 1] && <Divider sx={{ mt: 1, mb: 1, borderStyle: 'dashed' }} />}
        </Box>
      ))}
    </Box>
  );
};

export default ReportCards;
